import React from 'react';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import { toGlobalId } from 'graphql-relay';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import Avatar from 'four-nets/avatar/avatar';
import LocationIcon from 'four-nets/icons/svg/icon_place.svg';
import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import isPrArticleUsername from 'four-nets/common/is-pr-article-username';

import query, { ThreadVendorsPanelQuery } from './thread-vendors-panel.graphql';
import defaultStyles from './thread-vendors-panel.scss';

export interface Props {
    styles: typeof defaultStyles;
    threadCategoryId: string;
}

const ThreadVendorsPanel: React.FC<Props> = props => {
    const { styles, threadCategoryId } = props;

    const { data } = useQuery<ThreadVendorsPanelQuery>(query, {
        variables: { threadCategoryId: toGlobalId('ThreadCategoryNode', threadCategoryId) },
        skip: !threadCategoryId,
    });

    if (!data || !data.threadVendors || !data.threadVendors.edges) {
        return null;
    }

    const edges = data!.threadVendors!.edges!;

    return edges && edges.length ? (
        <div className={styles.wrapper}>
            <Link to={'/catalog/'} className={styles.wrapperTitle}>
                <FormattedMessage id='THREAD_PAIRED_VENDORS' />
            </Link>
            {edges.map(edge => {
                if (!edge || !edge.node) {
                    return null;
                }
                const { id, name, user, county, cardText } = edge.node;

                return (
                    <div className={styles.row} key={id}>
                        <div className={styles.flex}>
                            <Avatar style={{ avatar: styles.avatar }} user={user} />
                            <div className={styles.info}>
                                <Link className={styles.vendorName} to={`/catalog/vendor/${user.username}/`}>
                                    {name}
                                </Link>
                                {county ? (
                                    <div className={styles.flex}>
                                        <SVGIcon style={{ image: styles.icon }} src={LocationIcon} />
                                        <span className={styles.location}>{county.name}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className={styles.cardText}>
                            <TextShortener text={cardText || ''} words={30}>
                                {text => (
                                    <EnhancedText
                                        followLink={
                                            user?.profile?.vendor?.isPresentationPaid ||
                                            isPrArticleUsername(user?.username)
                                        }
                                    >
                                        {text}
                                    </EnhancedText>
                                )}
                            </TextShortener>
                        </div>
                    </div>
                );
            })}
        </div>
    ) : null;
};

export default Styled(defaultStyles)(ThreadVendorsPanel);
